import React from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useLocation, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Get, Put } from '@app/utils/httpProvider';
import { TRICHYEU_API } from '@app/utils/apiUrl';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { messageError, messageToast, status200 } from '../../../../utils/define';
import { URI_INTERNAL } from '../../../../utils/pathLocations';
import HorizontalFieldsRow from '@app/components/common/HorizontalFieldsRow';
import Editor from '@app/components/editor/Editor';

const schema = yup.object().shape({
    codeTrichYeu: yup.string().required(messageError.emptyMessage).max(50, 'Mã trích yếu không được vượt quá 50 ký tự'),
    soQuyenDinh: yup.string().required(messageError.emptyMessage).max(255, 'Số quyết định không được vượt quá 255 ký tự')
});

const TrichYeuEdit = () => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const location = useLocation();
    const params = new URLSearchParams(location.search.substring(1));
    const itemId = params.get('id');

    const itemQuery = useQuery(['trichYeu', itemId], () => Get(TRICHYEU_API.GET_ITEM, itemId), {
        enabled: !!itemId
    });

    const editMutation = useMutation((value) => Put(TRICHYEU_API.UPDATE_ITEM, itemId, value));

    const handleEdit = (values) => {
        const model = { ...values };

        editMutation.mutate(model, {
            onSuccess: (res) => {
                if (res.status !== status200) {
                    toast.error(messageToast.messageToastUpdateError);
                } else {
                    toast.success(messageToast.messageToastUpdateSuccess);
                    queryClient.invalidateQueries(['trichYeu', itemId]);
                    queryClient.invalidateQueries('trichYeuData');
                    history.push(URI_INTERNAL.TRICHYEU_PATH);
                }
            },
            onError: (err) => {
                toast.error(err?.message || 'Đã có lỗi xảy ra, vui lòng thử lại !');
            }
        });
    };

    if (itemQuery.isLoading) {
        return <div>Đang tải....</div>;
    }

    if (itemQuery.isError || !itemQuery.data) {
        return <div>Có lỗi xảy ra hoặc không tìm thấy trích yếu</div>;
    }

    const currentData = itemQuery.data;

    return (
        <Card>
            <Card.Header as="h5">Sửa trích yếu</Card.Header>
            <Card.Body>
                <Formik
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={(values) => handleEdit(values)}
                    initialValues={{
                        codeTrichYeu: currentData.codeTrichYeu || currentData.CodeTrichYeu || '',
                        soQuyenDinh: currentData.soQuyenDinh || currentData.SoQuyenDinh || '',
                        description: currentData.description || currentData.Description || ''
                    }}
                >
                    {({ handleSubmit, handleChange, values, errors, setFieldValue }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Mã trích yếu',
                                        name: 'codeTrichYeu',
                                        value: values.codeTrichYeu,
                                        onChange: handleChange,
                                        isInvalid: errors.codeTrichYeu,
                                        error: errors.codeTrichYeu
                                    },
                                    {
                                        label: 'Số quyết định',
                                        name: 'soQuyenDinh',
                                        value: values.soQuyenDinh,
                                        onChange: handleChange,
                                        isInvalid: errors.soQuyenDinh,
                                        error: errors.soQuyenDinh
                                    },
                                ]}
                            />
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Mô tả
                                </Form.Label>
                                <Col sm="10">
                                    <Col sm="10">
                                        <Editor
                                            name="description"
                                            value={values.description}
                                            onChange={(value) => {
                                                setFieldValue(
                                                    'description',
                                                    value
                                                );
                                            }}
                                        />
                                    </Col>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3">
                                <Col md={{ span: 10, offset: 2 }}>
                                    <ButtonGroup className="mb-2">
                                        <Button type="submit" theme="primary" disabled={editMutation.isLoading}>
                                            Cập nhật
                                        </Button>
                                        &nbsp;&nbsp;&nbsp;
                                        <Button onClick={() => history.push(URI_INTERNAL.TRICHYEU_PATH)} theme="secondary">
                                            Hủy
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                            </Form.Group>
                        </Form>
                    )}
                </Formik>
            </Card.Body>
        </Card>
    );
};

export default TrichYeuEdit;
